import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavBar } from '../../../NavBarContext';
import { useUserArea, useUserPermissions, useUser } from '../../../contexts/UserContext'; // ✅ Usar contexto
import useI18n from '../../hooks/i18n/useI18n';
import { pathToOption, optionToPath, areaOptions, RouteOption } from '../../../config/routes/routeConfig';
import { navBarStyles as styles } from '../../styles/navBarStyles';
import {
	handleLogout as handleLogoutHelper,
	getHasESGPermissions,
	getFilteredOptions,
} from '../../utils/navBarHelpers';

interface NavBarProps {
	onLogout?: () => Promise<void>;
}

const NavBar: React.FC<NavBarProps> = ({ onLogout }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { selectedOption, setSelectedOption } = useNavBar();
	const { area } = useUserArea();
	const { permissions } = useUserPermissions();
	const { clearUserData } = useUser(); // ✅ Obtener clearUserData del contexto
	const [showMenu, setShowMenu] = useState<boolean>(false);
	const { t, languageError } = useI18n();

	useEffect(() => {
		const activeOption = pathToOption[location.pathname];
		if (activeOption) setSelectedOption(activeOption);
	}, [location.pathname, setSelectedOption]);

	// ✅ OPTIMIZACIÓN: Memoizar cálculo de permisos ESG
	const hasESGPermissions = useMemo(
		() => getHasESGPermissions(area, permissions),
		[area, permissions]
	);

	// ✅ OPTIMIZACIÓN: Memoizar filtrado de opciones
	const filteredOptions = useMemo(
		() => getFilteredOptions(area, hasESGPermissions, areaOptions),
		[area, hasESGPermissions]
	);

	// ✅ OPTIMIZACIÓN: Memoizar handler de click en opciones
	const handleOptionClick = useCallback((option: RouteOption): void => {
		setSelectedOption(option);
		const path = optionToPath[option];
		if (path) navigate(path);
	}, [setSelectedOption, navigate]);

	// ✅ OPTIMIZACIÓN: Memoizar handler de logout
	const handleLogoutClick = useCallback(async (): Promise<void> => {
		if (onLogout) {
			await onLogout();
		}
		// ✅ Orden correcto: setSelectedOption, navigate, clearUserData
		await handleLogoutHelper(setSelectedOption, navigate, clearUserData);
	}, [onLogout, setSelectedOption, navigate, clearUserData]);

	// ✅ OPTIMIZACIÓN: Handler único para todos los botones (evita inline functions)
	const handleButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>): void => {
		const option = e.currentTarget.dataset.option as RouteOption;
		if (option) handleOptionClick(option);
	}, [handleOptionClick]);

	// Si hay un error con el idioma, mostramos un error
	if (languageError) {
		return (
			<div
				className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative'
				role='alert'>
				<p>{languageError}</p>
			</div>
		);
	}

	return (
		<nav className={styles.container}>
			<div className={styles.wrapper}>
				<div className={styles.mainDiv}>
					{/* Logo */}
					<div className={styles.logoContainer}>
						<img
							src='/logo-app.png'
							alt='MineESG Logo'
							className={styles.logo}
						/>
					</div>

					{/* Navegación Central */}
					<div className={styles.navContainer}>
						<div className={styles.buttonContainer}>
							{filteredOptions.map((option) => (
								<button
									key={option}
									data-option={option}
									onClick={handleButtonClick}
									className={styles.navButton(selectedOption === option)}>
									{t(`navBar.${option}`)}
								</button>
							))}
						</div>
					</div>

					{/* Perfil */}
					<div className={styles.profileContainer}>
						<div className='relative'>
							<button
								onClick={() => setShowMenu(!showMenu)}
								className={styles.profileButton}>
								<img
									src='/account_circle.svg'
									alt={t('navBar.profile')}
									className={styles.profileImage}
								/>
								<span className={styles.profileName}>
									{t('navBar.profile')}
								</span>
							</button>

							{showMenu && (
								<div className={styles.dropdownMenu}>
									<button
										onClick={handleLogoutClick}
										className={styles.logoutButton}>
										<span className='flex-1'>{t('navBar.logout')}</span>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</nav>
	);
};

export default NavBar;
